import { DotNotation } from '@/types/use';
import { TRPCQueryProcedure, TRPCRouterRecord } from '@trpc/server';
import { Key } from 'react';
import { z } from 'zod';
import {
    GetByIdSchema,
    PageQuerySchema,
    SearchQuerySchema,
    TreeQuerySchema,
} from './base-zod-schema';

// OperatorType enum
export enum OperatorType {
    /**So sánh bằng */
    Equal,
    /**So sánh hơn */
    GreaterThan,
    /**So sánh nhỏ ơn  */
    LessThan,
    /**So sánh in */
    In,
    /**So sánh bất kỳ */
    AnyEqual,
    /**So sánh regex có dấu. Nếu có hỗ trợ không dấu, auto detect */
    Regex,
    /**So sánh nhỏ hơn hoặc bằng */
    LessThanOrEqual,
    /**So sánh lớn hơn hoặc bằng */
    GreaterThanOrEqual,
    /**So sánh không bằng */
    NotEqual,
    /**So sánh not in */
    NotIn,
    /**So sánh regex có dấu và không dấu, sẽ đi search hết tất cả các cột được phép trên table */
    RegexAll,
}

// LogicalOperator enum
export enum LogicalOperator {
    And = 0,
    Or = 1,
}

// ChildLogicalOperator enum
export enum ChildLogicalOperator {
    And = 0,
    Or = 1,
}

export type BaseDT = Partial<{
    ID: string | null;
    StaleAt: string;
    Created: string;
    LastModified: string;
}>;

// TwLength type
export type TwLength<Min extends number, Max extends number, T> = T;
export type Nullable<T> = T | null;

export interface FilterBuilders<T extends {}> {
    logicalOperator: LogicalOperator;
    key?: DotNotation<T>;
    values?: (Key | boolean | null)[];
    operatorType?: OperatorType;
    childLogicalOperator?: ChildLogicalOperator;
    filters?: FilterBuilders<T>[];
    isNot?: boolean;
}

export interface FilterZodSchema {
    logicalOperator: number;
    key?: string;
    values?: (Key | boolean | null)[];
    operatorType?: number;
    isNot?: boolean;
    childLogicalOperator?: number;
    filters?: FilterZodSchema[];
}

/**Base server response return form httpclient */
export interface BaseServerResponse<T = any> {
    status: number;
    statusText: string;
    isSuccessStatus: boolean;
    data?: T;
    errors?: string[];

    errorKeys?: string[];
    _originError?: any;
}

export interface PageServerResponse<T> {
    Items: T[];
    HasNextPage: boolean;
    HasPreviousPage: boolean;
    PageNumber: number;
    TotalCount: number;
    TotalPages: number;
}

export interface JsonStringWithHint {
    valueJson?: string;
    hint?: string;
}

export class GetAllServerResponse<T> {
    Result: T[] = [];
    Total: number = 0;
}

export class TreeResultResponse<T> {
    Result: T[] = [];
    Total: number = 0;
}

export class SearchlResultResponse<T> {
    Items: T[] = [];
    Total: number = 0;
    IsCountAll: boolean = false;
}

export class CheckExistResponse<T> {
    Item: Nullable<T> = null;
    Result: boolean = false;
}

export class GetByIdResponse<T> {
    Result: Nullable<T> = null;
}

// SearchServerResponse class
export class SearchServerResponse<T> {
    Items: T[] = [];
    Total: number = 0;
    IsCountAll: boolean = false;
}

// UpdateServerResponse class
export class UpdateServerResponse {
    successed: boolean = false;
    errors: string[] = [];
}

// DeleteServerResponse class
export class DeleteServerResponse extends UpdateServerResponse { }

export class EndpointNames {
    public static Get = 'Get';
    public static GetSingle = 'GetSingle';
    public static GetById(id: string) {
        return `GetById`;
    }
    public static Search = 'Search';
    public static Page = 'Page';
    public static CheckExist = 'CheckExist';
    public static ExportExcel = 'ExportExcel';
    public static ExportExcelAdvance = 'ExportExcelAdvance';
    public static Tree = 'Tree';
    //
    public static Create = 'Create';
    public static Update(id: string) {
        return `Update`;
    }
    public static Delete(id: string) {
        return `Delete/${id}`;
    }
    public static CreateBatch = 'Creates';
    public static UpdateBatch = 'Updates';
    public static DeleteBatch = 'Deletes';
    public static Modify = 'Modify';
    public static Sort = 'Sort';
    //
    public static WithFile = 'wf';
    public static CreateWithFile = 'Create/' + this.WithFile;
    public static UpdateWithFile(id: string) {
        return `Update/${this.WithFile}`;
    }
    public static CreateBatchWithFile = 'Creates/' + this.WithFile;
    public static UpdateBatchWithFile = `Updates/${this.WithFile}`;
    public static ModifyWithFile = 'Modify/' + this.WithFile;
}

/**
 * Services API
 *
 * Cac đầu API cơ bản của 1 controller
 *
 * Khai báo 2 đầu khác nhau: Query và Mutation
 */
export default interface BaseTrpcService<T> extends TRPCRouterRecord {
    /**
     * Query
     */
    GetAll: TRPCQueryProcedure<{
        meta: unknown;
        input: z.infer<typeof PageQuerySchema>;
        output: BaseServerResponse<GetAllServerResponse<T>>;
    }>;
    GetSingle: TRPCQueryProcedure<{
        meta: unknown;
        input: z.infer<typeof PageQuerySchema>;
        output: BaseServerResponse<GetByIdResponse<T>>;
    }>;
    GetById: TRPCQueryProcedure<{
        meta: unknown;
        input: z.infer<typeof GetByIdSchema>;
        output: BaseServerResponse<GetByIdResponse<T>>;
    }>;
    GetPage: TRPCQueryProcedure<{
        meta: unknown;
        input: z.infer<typeof PageQuerySchema>;
        output: BaseServerResponse<PageServerResponse<T>>;
    }>;
    GetTree: TRPCQueryProcedure<{
        meta: unknown;
        input: z.infer<typeof TreeQuerySchema>;
        output: BaseServerResponse<TreeResultResponse<T>>;
    }>;
    Search: TRPCQueryProcedure<{
        meta: unknown;
        input: z.infer<typeof SearchQuerySchema>;
        output: BaseServerResponse<SearchlResultResponse<T>>;
    }>;
    CheckExist: TRPCQueryProcedure<{
        meta: unknown;
        input: z.infer<typeof PageQuerySchema>;
        output: BaseServerResponse<CheckExistResponse<T>>;
    }>;
}
